/**
 * The status a reviewer reads for a case, in one place.
 *
 * The case store keeps a lifecycle state (`draft-ready`, `needs-review`, ...) and a
 * route (`quote-draft`, `ask-customer`, ...). Since stage 2 the server also
 * derives where the case stands for sales (`work_status`): replied, waiting for the
 * customer, done, not for us, or closed by a sent quotation. The queue, the case
 * review and the add-in print the business wording derived from all three.
 *
 * An open case (or one from a backend without `work_status`) reads exactly as before.
 *
 * A reason of the entity rules that holds the case wins over "Ready to quote": the
 * stored state can predate a price rule, and a held case is never shown as ready.
 */

import type { CaseRules, WorkStatus } from "@/lib/api";
import { isOutOfScopeCase, isParkedIntent, NOT_AN_INQUIRY } from "@/lib/intents";
import type { CaseState } from "@/lib/mock";

export type CaseStatusTone = "neutral" | "info" | "warning" | "success" | "accent";

export interface CaseStatus {
  label: string;
  tone: CaseStatusTone;
}

const WORK_STATUS: Record<Exclude<WorkStatus, "open">, CaseStatus> = {
  replied: { label: "Replied", tone: "info" },
  waiting_customer: { label: "Waiting for customer", tone: "warning" },
  done: { label: "Done", tone: "success" },
  not_for_us: { label: "Not for us", tone: "neutral" },
  auto_closed: { label: "Closed", tone: "neutral" },
  // While the switch `autonomy_close_no_activity` is on (ON since 11 Oct 2026, 21 days).
  no_activity: { label: "No activity", tone: "neutral" },
};

/**
 * One label per status, used by the pills, the tabs, the built-in views and the rows.
 * "Needs a check" covers every open case where a person has to look at a line or a check.
 */
export const STATUS_WORDS = {
  ready: "Ready to quote",
  check: "Needs a check",
  customer: "Customer to confirm",
  replied: "Replied",
  waiting: "Waiting for customer",
  waitingForI2Q: "Waiting for I2Q",
} as const;

/** The label of each work status, for a test that one status maps to one label. */
export function workStatusLabel(status: Exclude<WorkStatus, "open">): string {
  return WORK_STATUS[status].label;
}

/** The `review_reason` the server writes when it could not process a mail (store/records.py). */
export const REASON_ENGINE_FAILED = "The engine could not process this mail";
/** The `review_reason` for an inquiry whose customer was not found at all. */
export const REASON_CUSTOMER = "Customer not identified";
/** Status of a purchase order: out of the Norican I2Q scope, counted but not worked on. */
export const PARKED_LABEL = "Purchase order, not worked on";

/** The status of a case held by a rule, from its first blocking reason. */
const HELD_STATUS: Record<string, CaseStatus> = {
  "pricing.margin": { label: "Price approval needed", tone: "warning" },
  "pricing.no_price": { label: "Held: no price", tone: "warning" },
};
const HELD_OTHER: CaseStatus = { label: "Held by a rule", tone: "warning" };

/** The held status when a rule holds the case, else null. */
export function heldStatusOf(rules: CaseRules | null | undefined): CaseStatus | null {
  const first = (rules?.reasons ?? []).find((reason) => reason.severity === "blocking");
  if (!first) {
    return null;
  }
  return HELD_STATUS[first.rule_id] ?? HELD_OTHER;
}

/**
 * Why an open case needs a person, in the words of the queue tabs:
 *   - I2Q could not process the mail → "Not processed";
 *   - no customer was found → "Customer not identified"; a customer is suggested or
 *     needs confirming → "Customer to confirm";
 *   - a line or a check is waiting for a person → "Needs a check". `linesOpen` says
 *     which of the two: an open line comes before a rule that holds the case.
 */
function needsReviewStatus(row: {
  route?: string | null;
  reviewReason?: string | null;
  linesAuto?: number | null;
  lineCount?: number | null;
}): CaseStatus & { linesOpen?: boolean } {
  if (row.reviewReason === REASON_ENGINE_FAILED) {
    return { label: "Not processed", tone: "neutral" };
  }
  if (row.route === "ask-customer") {
    return row.reviewReason === REASON_CUSTOMER
      ? { label: "Customer not identified", tone: "warning" }
      : { label: STATUS_WORDS.customer, tone: "warning" };
  }
  if (row.linesAuto != null && row.lineCount != null && row.linesAuto < row.lineCount) {
    return { label: STATUS_WORDS.check, tone: "warning", linesOpen: true };
  }
  return { label: STATUS_WORDS.check, tone: "warning" };
}

export function caseStatusOf(row: {
  state: CaseState;
  route?: string | null;
  workStatus?: WorkStatus | string | null;
  reviewReason?: string | null;
  linesAuto?: number | null;
  lineCount?: number | null;
  engineIntent?: string | null;
  intent?: string | null;
  /** The entity rules on the case (case page); a blocking reason wins over "matched". */
  rules?: CaseRules | null;
}): CaseStatus {
  // Out of the Norican I2Q scope wins over any work status or older engine state.
  if (row.state !== "parked" && row.state !== "not-an-rfq" && isOutOfScopeCase(row)) {
    return isParkedIntent(row.engineIntent)
      ? { label: PARKED_LABEL, tone: "neutral" }
      : { label: NOT_AN_INQUIRY, tone: "neutral" };
  }
  if (typeof row.workStatus === "string" && Object.hasOwn(WORK_STATUS, row.workStatus)) {
    return WORK_STATUS[row.workStatus as keyof typeof WORK_STATUS];
  }
  switch (row.state) {
    case "draft-ready":
      return heldStatusOf(row.rules) ?? { label: STATUS_WORDS.ready, tone: "success" };
    case "needs-review": {
      const { linesOpen, ...status } = needsReviewStatus(row);
      // A failed read, the customer and an open line come first; then the rule that holds it.
      return status.label === STATUS_WORDS.check && !linesOpen
        ? (heldStatusOf(row.rules) ?? status)
        : status;
    }
    case "queued":
      return { label: STATUS_WORDS.waitingForI2Q, tone: "neutral" };
    case "processing":
      return { label: "Processing", tone: "info" };
    case "not-an-rfq":
      return { label: NOT_AN_INQUIRY, tone: "neutral" };
    case "parked":
      return { label: PARKED_LABEL, tone: "neutral" };
    case "duplicate":
      return { label: "Copy of another mail", tone: "neutral" };
    case "in-progress":
      return { label: "In progress", tone: "accent" };
    case "auto-closed":
      return { label: "Closed", tone: "neutral" };
  }
}

/** What the lines counter of a case knows (queue row or case page). */
export interface LinesCount {
  /** Lines read from the mail. */
  lineCount: number | null | undefined;
  /** Lines matched with certainty. */
  linesAuto: number | null | undefined;
  /** Lines with an item, matched or suggested; null when the server did not say. */
  itemsFound?: number | null;
  /** True when the customer is still to confirm: then a suggested item is not "matched". */
  customerToConfirm?: boolean;
  /** Lines a sent quotation confirmed (same item quoted), with its number. */
  confirmedBy?: { lines: number; doc: string } | null;
}

/**
 * The lines counter: "3 of 5 matched"; "14 of 15 items found, customer to confirm" while
 * the customer is open (the items wait for the customer, they are not missing); on a
 * case the sent quotation confirmed, "1 of 1, confirmed by quotation SQ044478". Null
 * without lines.
 */
export function linesCountText(count: LinesCount): string | null {
  const total = count.lineCount;
  if (total == null || total === 0) {
    return null;
  }
  if (count.confirmedBy && count.confirmedBy.lines > 0) {
    const confirmed = Math.min(total, Math.max(count.linesAuto ?? 0, count.confirmedBy.lines));
    return `${confirmed} of ${total}, confirmed by quotation ${count.confirmedBy.doc}`;
  }
  if (count.customerToConfirm && count.itemsFound != null) {
    return `${Math.min(count.itemsFound, total)} of ${total} items found, customer to confirm`;
  }
  if (count.linesAuto == null) {
    return `${total} ${total === 1 ? "line" : "lines"}`;
  }
  return `${count.linesAuto} of ${total} matched`;
}

/** "Also received in Sales-UK@norican.com" (several joined), for the same mail elsewhere. */
export function alsoReceivedText(mailboxes: readonly string[]): string {
  return mailboxes.length > 0
    ? `Also received in ${mailboxes.join(", ")}`
    : "Also received in another mailbox";
}
